import * as req from 'airship/modules/request2'
import createRequest from 'airship/modules/request2/ua-request'
import throwIfNotSuccess from '../request2/utils/throwIfNotSuccess'

export type Assertion<T> = (object: unknown) => T
export type Options = req.Options & {responseCodeErrorCutoff?: number}

export type TypesafeRequester = {
  /**
   * Perform a GET request and verify the response type
   * @param assertion Assertion function for response body
   * @param opts Request options
   * @throws {RequestError} if response code is not successful
   * @throws {TypeGuardError} if response body does not match interface
   */
  get<T>(assertion: Assertion<T>, opts?: Options): Promise<req.Response<T>>
  /**
   * Perform a POST request and verify the response type
   * @param assertion Assertion function for response body
   * @param opts Request options
   * @throws {RequestError} if response code is not successful
   * @throws {TypeGuardError} if response body does not match interface
   */
  post<T>(assertion: Assertion<T>, opts?: Options): Promise<req.Response<T>>
  /**
   * Perform a PUT request and verify the response type
   * @param assertion Assertion function for response body
   * @param opts Request options
   * @throws {RequestError} if response code is not successful
   * @throws {TypeGuardError} if response body does not match interface
   */
  put<T>(assertion: Assertion<T>, opts?: Options): Promise<req.Response<T>>
  /**
   * Perform a PATCH request and verify the response type
   * @param assertion Assertion function for response body
   * @param opts Request options
   * @throws {RequestError} if response code is not successful
   * @throws {TypeGuardError} if response body does not match interface
   */
  patch<T>(assertion: Assertion<T>, opts?: Options): Promise<req.Response<T>>
  /**
   * Perform a DELETE request and verify the response type
   * @param assertion Assertion function for response body
   * @param opts Request options
   * @throws {RequestError} if response code is not successful
   * @throws {TypeGuardError} if response body does not match interface
   */
  delete<T>(assertion: Assertion<T>, opts?: Options): Promise<req.Response<T>>
  end: () => void
}

export type Response<T> = req.Response<T>
export type KeyValue = req.KeyValue

/**
 * Creates a typesafe-requester
 * @param baseOpts Request options
 */
export default function typesafeRequest(
  baseOpts?: req.Options
): TypesafeRequester {
  const request = createRequest(baseOpts)
  const original = {
    get: request.get,
    post: request.post,
    put: request.put,
    patch: request.patch,
    delete: request.delete,
  }

  return {
    get: wrapMethod('get'),
    post: wrapMethod('post'),
    put: wrapMethod('put'),
    patch: wrapMethod('patch'),
    delete: wrapMethod('delete'),
    end: () => request.end(),
  }

  function wrapMethod<Response>(
    methodName: 'get' | 'post' | 'put' | 'delete' | 'patch'
  ): (
    assertion: Assertion<Response>,
    opts?: Options
  ) => Promise<req.Response<Response>> {
    return async function makeRequest(
      assertion: Assertion<Response>,
      opts?: Options
    ): Promise<req.Response<Response>> {
      const resp = await original[methodName](opts ?? {})

      throwIfNotSuccess(resp, opts?.responseCodeErrorCutoff)
      assertion(resp.body) // Will throw if unsuccessful
      return resp
    }
  }
}
